import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage, getStatusLabel } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';
import { FileText, Plus, Eye, CreditCard, Link as LinkIcon, Copy, DownloadSimple, ShareNetwork, Wallet, Trash, EyeSlash } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api, { getInvoices, createInvoice, updateInvoiceStatus, getCustomers, getPackages, createPaymentCheckout, downloadInvoicePdf, getPublicInvoicePdfUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UpgradeBanner } from '../components/UpgradeBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';

export default function InvoicesPage() {
  const { t, language , tx } = useLanguage();
  const { hasFeature } = usePlan();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [transportFee, setTransportFee] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [invoiceCustomerFilter, setInvoiceCustomerFilter] = useState('all');
  const [invoicePackages, setInvoicePackages] = useState([]);
  const [loadingInvoicePackages, setLoadingInvoicePackages] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter, invoiceCustomerFilter]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerPackages(selectedCustomer);
    }
  }, [selectedCustomer]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (invoiceCustomerFilter !== 'all') params.customer_id = invoiceCustomerFilter;
      const [invoicesRes, customersRes] = await Promise.all([
        getInvoices(params),
        getCustomers()
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error(tx('Failed to fetch data', 'Error al cargar datos', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerPackages = async (customerId) => {
    try {
      const response = await getPackages({ customer_id: customerId });
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (selectedPackages.length === 0) {
      toast.error(tx('Select at least one package', 'Selecciona al menos un paquete', 'Select at least one package'));
      return;
    }
    try {
      await createInvoice({
        customer_id: selectedCustomer,
        package_ids: selectedPackages,
        due_date: dueDate,
        transport_fee: transportFee ? parseFloat(transportFee) : 0,
        notes: notes || null
      });
      toast.success(tx('Invoice created', 'Factura creada', 'Facture créée'));
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Failed to create invoice', 'Error al crear factura', 'Failed to create invoice')));
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    if (newStatus === 'delivered') {
      const inv = invoices.find(i => i.id === invoiceId);
      const pkgCount = (inv?.package_ids || []).length;
      if (!window.confirm(tx(
        `Mark invoice ${inv?.invoice_number} as DELIVERED?\n\nAll ${pkgCount} package(s) on this invoice will also be marked as delivered.`,
        `¿Marcar factura ${inv?.invoice_number} como ENTREGADA?\n\nLos ${pkgCount} paquete(s) también se marcarán como entregados.`,
        `Marquer la facture ${inv?.invoice_number} comme LIVRÉE ? Tous les ${pkgCount} colis seront également livrés.`
      ))) return;
    }
    try {
      await updateInvoiceStatus(invoiceId, newStatus);
      toast.success(tx('Status updated', 'Estado actualizado', 'Status updated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || tx('Failed to update', 'Error al actualizar', 'Failed to update'));
    }
  };

  const handleCancelInvoice = async (invoice) => {
    if (!window.confirm(tx(
      `Remove invoice ${invoice.invoice_number}? It will be marked Cancelled and its packages will be released so you can re-invoice them. No data is deleted.`,
      `¿Quitar la factura ${invoice.invoice_number}? Se marcará como Cancelada y los paquetes quedarán disponibles para volver a facturar. No se elimina nada.`,
      `Annuler la facture ${invoice.invoice_number} ? Elle sera marquée Annulée et les colis seront libérés.`
    ))) return;
    try {
      await updateInvoiceStatus(invoice.id, 'cancelled');
      toast.success(tx('Invoice removed', 'Factura quitada', 'Facture annulée'));
      fetchData();
    } catch (error) {
      toast.error(tx('Failed to remove invoice', 'Error al quitar factura', 'Échec'));
    }
  };

  const handlePayment = async (invoiceId, method = 'stripe') => {
    try {
      const response = await createPaymentCheckout(invoiceId, method);
      window.open(response.data.checkout_url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Failed to create payment', 'Error al crear pago', 'Failed to create payment')));
    }
  };

  const copyPaymentLink = async (invoiceId, method = 'stripe') => {
    try {
      const response = await createPaymentCheckout(invoiceId, method);
      await navigator.clipboard.writeText(response.data.checkout_url);
      toast.success(tx('Link copied', 'Link copiado', 'Link copied'));
    } catch (error) {
      toast.error(tx('Failed to copy link', 'Error al copiar link', 'Failed to copy link'));
    }
  };

  const resetForm = () => {
    setSelectedCustomer('');
    setSelectedPackages([]);
    setDueDate('');
    setNotes('');
    setTransportFee('');
    setPackages([]);
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await downloadInvoicePdf(invoiceId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Track_My_Package_Invoice.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(tx('PDF downloaded', 'PDF descargado', 'PDF downloaded'));
    } catch (error) {
      toast.error(tx('Failed to download PDF', 'Error al descargar PDF', 'Failed to download PDF'));
    }
  };

  const handleShareInvoice = async (invoiceNumber) => {
    const publicUrl = `${window.location.origin}/invoice/${invoiceNumber}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(tx('Invoice link copied', 'Link de factura copiado', 'Invoice link copied'));
    } catch {
      toast.error(tx('Failed to copy', 'Error al copiar', 'Failed to copy'));
    }
  };

  const togglePackage = (packageId) => {
    setSelectedPackages(prev => 
      prev.includes(packageId) 
        ? prev.filter(id => id !== packageId)
        : [...prev, packageId]
    );
  };

  // A package is "pending" (invoiceable) if it has NO active invoice attached
  // AND it's not already delivered/closed-out. Delivered packages are treated
  // as cash hand-offs already settled — they must NOT be re-invoiced or counted
  // toward the customer's outstanding balance (matches recompute_balance logic).
  const isPendingForInvoice = (pkg) => !pkg.invoice_id && pkg.status !== 'delivered';

  const pendingPackages = packages.filter(isPendingForInvoice);

  const selectAllPending = () => {
    setSelectedPackages(pendingPackages.map(p => p.id));
  };

  const clearSelection = () => {
    setSelectedPackages([]);
  };

  const getStatusBadgeClass = (status) => `status-${status}`;

  const calculateSelectedTotal = () => {
    return packages
      .filter(p => selectedPackages.includes(p.id))
      .reduce((sum, p) => sum + (p.shipping_cost || 0) + (p.transport_fee || 0), 0)
      .toFixed(2);
  };

  if (!hasFeature('invoices')) {
    return (
      <Layout title={t('invoices')}>
        <UpgradeBanner feature="Invoicing" message="Invoice generation, PDF exports, and Stripe payment links are available on the Professional plan." />
      </Layout>
    );
  }

  return (
    <Layout title={t('invoices')}>
      <div className="space-y-6 premium-fade-up" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-lg">
              <SelectValue placeholder={tx('All Status', 'Todos', 'All Status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tx('All Status', 'Todos', 'All Status')}</SelectItem>
              <SelectItem value="pending">{tx('Pending', 'Pendiente', 'Pending')}</SelectItem>
              <SelectItem value="paid">{tx('Paid', 'Pagado', 'Paid')}</SelectItem>
              <SelectItem value="overdue">{tx('Overdue', 'Vencido', 'Overdue')}</SelectItem>
              <SelectItem value="cancelled">{tx('Cancelled', 'Cancelado', 'Cancelled')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={invoiceCustomerFilter} onValueChange={setInvoiceCustomerFilter}>
            <SelectTrigger className="w-52 rounded-lg" data-testid="invoice-customer-filter">
              <SelectValue placeholder={tx('All Clients', 'Todos los Clientes', 'Tous Clients')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tx('All Clients', 'Todos los Clientes', 'Tous Clients')}</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none" data-testid="toggle-show-cancelled">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="rounded"
            />
            {tx('Show cancelled', 'Mostrar canceladas', 'Afficher annulées')}
          </label>
          <Button 
            onClick={() => { resetForm(); setShowModal(true); }}
            className="ship-gradient text-white rounded-lg"
            data-testid="create-invoice-btn"
          >
            <Plus size={18} className="mr-2" />
            {t('newInvoice')}
          </Button>
        </div>

        {/* Invoices Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="kpi-card overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4">{t('invoiceNumber')}</th>
                    <th className="text-left py-3 px-4">{t('customer')}</th>
                    <th className="text-left py-3 px-4">{t('packages')}</th>
                    <th className="text-right py-3 px-4">USD</th>
                    <th className="text-right py-3 px-4">CRC</th>
                    <th className="text-left py-3 px-4">{t('status')}</th>
                    <th className="text-left py-3 px-4">{t('dueDate')}</th>
                    <th className="text-center py-3 px-4">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.filter(inv => showCancelled || statusFilter === 'cancelled' || inv.status !== 'cancelled').map((invoice) => (
                    <tr key={invoice.id} className={`border-b border-gray-100 hover:bg-gray-50 ${invoice.status === 'cancelled' ? 'opacity-60' : ''}`}>
                      <td className="py-3 px-4 font-mono text-sm font-medium">{invoice.invoice_number}</td>
                      <td className="py-3 px-4">{invoice.customer_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{invoice.package_ids.length}</td>
                      <td className="py-3 px-4 text-right font-bold">${invoice.total_usd}</td>
                      <td className="py-3 px-4 text-right text-gray-600">₡{invoice.total_crc?.toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <Select value={invoice.status} onValueChange={(status) => handleStatusChange(invoice.id, status)}>
                          <SelectTrigger className={`w-32 h-8 text-xs rounded-lg ${getStatusBadgeClass(invoice.status)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">{tx('Pending', 'Pendiente', 'Pending')}</SelectItem>
                            <SelectItem value="paid">{tx('Paid', 'Pagado', 'Paid')}</SelectItem>
                            <SelectItem value="delivered">{tx('Delivered', 'Entregado', 'Livré')}</SelectItem>
                            <SelectItem value="overdue">{tx('Overdue', 'Vencido', 'Overdue')}</SelectItem>
                            <SelectItem value="cancelled">{tx('Cancelled', 'Cancelado', 'Cancelled')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3 px-4 text-sm">{new Date(invoice.due_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={async () => {
                              setSelectedInvoice(invoice);
                              setShowDetailModal(true);
                              setInvoicePackages([]);
                              setLoadingInvoicePackages(true);
                              try {
                                const res = await api.get(`/invoices/${invoice.id}/packages`);
                                setInvoicePackages(res.data || []);
                              } catch { /* leave blank */ }
                              setLoadingInvoicePackages(false);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg"
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye size={18} className="text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDownloadPdf(invoice.id)}
                            className="p-1.5 hover:bg-teal-50 rounded-lg"
                            title={tx('Download PDF', 'Descargar PDF', 'Télécharger PDF')}
                            data-testid={`download-pdf-${invoice.id}`}
                          >
                            <DownloadSimple size={18} className="text-[#0077FF]" />
                          </button>
                          <button
                            onClick={() => handleShareInvoice(invoice.invoice_number)}
                            className="p-1.5 hover:bg-purple-50 rounded-lg"
                            title={tx('Share invoice', 'Compartir factura', 'Partager la Facture')}
                            data-testid={`share-invoice-${invoice.id}`}
                          >
                            <ShareNetwork size={18} className="text-purple-600" />
                          </button>
                          {invoice.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handlePayment(invoice.id, 'stripe')}
                                className="p-1.5 hover:bg-green-50 rounded-lg"
                                title="Pay with Stripe"
                                data-testid={`stripe-pay-${invoice.id}`}
                              >
                                <CreditCard size={18} className="text-green-600" />
                              </button>
                              {hasFeature('paypal_payment_links') && (
                                <button
                                  onClick={() => handlePayment(invoice.id, 'paypal')}
                                  className="p-1.5 hover:bg-blue-50 rounded-lg"
                                  title="Pay with PayPal"
                                  data-testid={`paypal-pay-${invoice.id}`}
                                >
                                  <Wallet size={18} className="text-blue-600" />
                                </button>
                              )}
                              <button
                                onClick={() => copyPaymentLink(invoice.id)}
                                className="p-1.5 hover:bg-blue-50 rounded-lg"
                                title={t('sendPaymentLink')}
                              >
                                <Copy size={18} className="text-blue-600" />
                              </button>
                            </>
                          )}
                          {invoice.status !== 'cancelled' && (
                            <button
                              onClick={() => handleCancelInvoice(invoice)}
                              className="p-1.5 hover:bg-red-50 rounded-lg"
                              title={tx('Remove invoice (release packages)', 'Quitar factura (liberar paquetes)', 'Annuler la facture')}
                              data-testid={`remove-invoice-${invoice.id}`}
                            >
                              <Trash size={18} className="text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t('noInvoices')}</p>
              <p className="text-sm mt-1">{t('createFirstInvoice')}</p>
            </div>
          )}
        </motion.div>

        {/* Create Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {t('newInvoice')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="form-label">{t('customer')}</Label>
                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder={t('selectCustomer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedCustomer && pendingPackages.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="form-label mb-0">{t('selectPackages')}</Label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAllPending}
                        className="text-xs font-semibold uppercase tracking-wide text-[#0077FF] hover:text-[#005FCC] transition"
                        data-testid="select-all-pending-btn"
                      >
                        {tx(
                          `Select All (${pendingPackages.length})`,
                          `Seleccionar Todos (${pendingPackages.length})`,
                          `Tout Sélectionner (${pendingPackages.length})`
                        )}
                      </button>
                      {selectedPackages.length > 0 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={clearSelection}
                            className="text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-700 transition"
                            data-testid="clear-selection-btn"
                          >
                            {tx('Clear', 'Limpiar', 'Effacer')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {pendingPackages.map(pkg => (
                      <label 
                        key={pkg.id}
                        className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedPackages.includes(pkg.id)}
                          onCheckedChange={() => togglePackage(pkg.id)}
                        />
                        <div className="flex-1">
                          <p className="font-mono text-sm">{pkg.tracking_number}</p>
                          <p className="text-xs text-gray-500">{pkg.description}</p>
                        </div>
                        <span className="font-medium">${((Number(pkg.shipping_cost) || 0) + (Number(pkg.transport_fee) || 0)).toFixed(2)}</span>
                      </label>
                    ))}
                  </div>
                  {selectedPackages.length > 0 && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        {selectedPackages.length} {tx('packages selected', 'paquetes seleccionados', 'packages selected')}
                      </span>
                      <span className="font-bold text-lg" data-testid="selected-total-amount">${calculateSelectedTotal()}</span>
                    </div>
                  )}
                </div>
              )}

              {selectedCustomer && packages.length > 0 && pendingPackages.length === 0 && (
                <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                  <p>{tx('All packages for this customer are already on an invoice', 'Todos los paquetes ya están en una factura', 'Tous les colis sont déjà facturés')}</p>
                </div>
              )}

              {selectedCustomer && packages.length === 0 && (
                <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                  <p>{tx('No packages for this customer', 'No hay paquetes para este cliente', 'No packages for this customer')}</p>
                </div>
              )}

              <div>
                <Label className="form-label">{t('dueDate')}</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="rounded-lg"
                  required
                />
              </div>

              <div>
                <Label className="form-label">
                  {tx('Transport Fee (optional)', 'Tarifa de Transporte (opcional)', 'Frais de transport (optionnel)')}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={transportFee}
                    onChange={(e) => setTransportFee(e.target.value)}
                    placeholder="0.00"
                    className="rounded-lg pl-7"
                    data-testid="invoice-transport-fee-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {tx(
                    'Flat fee added on top of all selected packages (e.g., final-mile delivery, handling).',
                    'Tarifa fija añadida a todos los paquetes (ej. entrega final, manejo).',
                    'Frais fixes ajoutés à tous les colis sélectionnés.'
                  )}
                </p>
              </div>

              {/* Running total preview */}
              {selectedPackages.length > 0 && (() => {
                const pkgSubtotal = selectedPackages.reduce((sum, pid) => {
                  const p = packages.find(x => x.id === pid);
                  return sum + (p?.shipping_cost || 0);
                }, 0);
                const pkgTransport = selectedPackages.reduce((sum, pid) => {
                  const p = packages.find(x => x.id === pid);
                  return sum + (p?.transport_fee || 0);
                }, 0);
                const flatFee = parseFloat(transportFee) || 0;
                const grandTotal = pkgSubtotal + pkgTransport + flatFee;
                return (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm space-y-1" data-testid="invoice-total-preview">
                    <div className="flex justify-between text-gray-700">
                      <span>{tx('Packages subtotal', 'Subtotal paquetes', 'Sous-total colis')}</span>
                      <span className="font-mono">${pkgSubtotal.toFixed(2)}</span>
                    </div>
                    {pkgTransport > 0 && (
                      <div className="flex justify-between text-gray-700">
                        <span>{tx('Per-package transport fees', 'Transporte por paquete', 'Transport / colis')}</span>
                        <span className="font-mono">${pkgTransport.toFixed(2)}</span>
                      </div>
                    )}
                    {flatFee > 0 && (
                      <div className="flex justify-between text-gray-700">
                        <span>{tx('Flat transport fee', 'Tarifa fija de transporte', 'Frais fixes')}</span>
                        <span className="font-mono">${flatFee.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-base text-[#0077FF] pt-1 border-t border-blue-200">
                      <span>{tx('Invoice Total', 'Total Factura', 'Total facture')}</span>
                      <span className="font-mono" data-testid="invoice-total-amount">${grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}

              <div>
                <Label className="form-label">{t('notes')}</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={tx('Additional notes...', 'Notas adicionales...', 'Additional notes...')}
                  className="rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-lg">
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="ship-gradient text-white rounded-lg" 
                  disabled={selectedPackages.length === 0}
                >
                  {t('create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Invoice Details', 'Detalles de Factura', 'Détails de la Facture')}
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4 mt-4">
                <div className="ship-gradient text-white p-4 rounded-xl">
                  <p className="text-xs uppercase tracking-wide text-white/70 mb-1">{t('invoiceNumber')}</p>
                  <p className="font-mono text-xl font-bold">{selectedInvoice.invoice_number}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('customer')}</p>
                    <p className="font-medium">{selectedInvoice.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('status')}</p>
                    <span className={`status-badge ${getStatusBadgeClass(selectedInvoice.status)}`}>
                      {getStatusLabel(selectedInvoice.status, language)}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('total')} USD</p>
                      <p className="text-2xl font-bold text-[#0077FF]">${selectedInvoice.total_usd}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('total')} CRC</p>
                      <p className="text-2xl font-bold text-[#FFB300]">₡{selectedInvoice.total_crc?.toLocaleString()}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('exchangeRate')}: ₡{selectedInvoice.exchange_rate}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">{t('packages')} ({selectedInvoice.package_ids.length})</p>
                  {loadingInvoicePackages ? (
                    <p className="text-xs text-gray-400">{tx('Loading…', 'Cargando…', 'Chargement…')}</p>
                  ) : invoicePackages.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden text-sm">
                      <table className="w-full">
                        <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                          <tr>
                            <th className="px-2 py-1.5 text-left">{t('trackingNumber')}</th>
                            <th className="px-2 py-1.5 text-left">{tx('Description', 'Descripción', 'Description')}</th>
                            <th className="px-2 py-1.5 text-right">{t('weight')}</th>
                            <th className="px-2 py-1.5 text-right">{t('cost')}</th>
                            <th className="px-2 py-1.5 text-center">{t('status')}</th>
                          </tr>
                        </thead>
                        <tbody data-testid="invoice-packages-list">
                          {invoicePackages.map(p => (
                            <tr key={p.id} className="border-t text-xs">
                              <td className="px-2 py-1.5 font-mono">{p.tracking_number}</td>
                              <td className="px-2 py-1.5 text-gray-700">{p.description || '—'}</td>
                              <td className="px-2 py-1.5 text-right">{p.weight_kg ? `${p.weight_kg} kg` : '—'}</td>
                              <td className="px-2 py-1.5 text-right font-semibold text-[#0077FF]">
                                ${((Number(p.shipping_cost) || 0) + (Number(p.transport_fee) || 0)).toFixed(2)}
                                {p.price_override && <span className="text-amber-600 ml-1" title={p.price_override_reason}>★</span>}
                              </td>
                              <td className="px-2 py-1.5 text-center text-[10px] text-gray-600">{p.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">{tx('No package details available', 'Sin detalles', 'Aucun colis')}</p>
                  )}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('dueDate')}</p>
                  <p className="font-medium">{new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                </div>

                {selectedInvoice.paid_at && (
                  <div className="text-sm text-green-600">
                    {tx('Paid on', 'Pagado el', 'Paid on')}: {new Date(selectedInvoice.paid_at).toLocaleString()}
                  </div>
                )}

                {selectedInvoice.status === 'pending' && (
                  <div className="flex flex-col gap-2 pt-4">
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handlePayment(selectedInvoice.id, 'stripe')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                        data-testid="detail-stripe-pay"
                      >
                        <CreditCard size={18} className="mr-2" />
                        Pay with Stripe
                      </Button>
                      {hasFeature('paypal_payment_links') && (
                        <Button 
                          onClick={() => handlePayment(selectedInvoice.id, 'paypal')}
                          className="flex-1 bg-[#0070BA] hover:bg-[#005EA6] text-white rounded-lg"
                          data-testid="detail-paypal-pay"
                        >
                          <Wallet size={18} className="mr-2" />
                          Pay with PayPal
                        </Button>
                      )}
                    </div>
                    <Button 
                      onClick={() => copyPaymentLink(selectedInvoice.id)}
                      variant="outline"
                      className="rounded-lg"
                    >
                      <Copy size={18} className="mr-2" />
                      {t('sendPaymentLink')}
                    </Button>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={() => handleDownloadPdf(selectedInvoice.id)}
                    variant="outline"
                    className="flex-1 rounded-lg border-[#0077FF] text-[#0077FF] hover:bg-teal-50"
                    data-testid="modal-download-pdf"
                  >
                    <DownloadSimple size={18} className="mr-2" />
                    {tx('Download PDF', 'Descargar PDF', 'Télécharger PDF')}
                  </Button>
                  <Button 
                    onClick={() => handleShareInvoice(selectedInvoice.invoice_number)}
                    variant="outline"
                    className="flex-1 rounded-lg border-purple-500 text-purple-600 hover:bg-purple-50"
                    data-testid="modal-share-invoice"
                  >
                    <ShareNetwork size={18} className="mr-2" />
                    {tx('Share', 'Compartir', 'Partager')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
